// @flow
import { base64Encode } from '@finect/front-utils/base64';
import ACTIONS from '../constants';
import type { StoreType } from './types';

/**
 * Codifica el body en base64 para que no haya problemas con caracteres especiales
 */
const contentModelize = (model: Object) =>
  (model.type === 'article'
    ? {
      ...model,
      body: {
        ...model.body,
        text: base64Encode(model.body.text)
      }
    }
    : model);

export function content(
  state: StoreType = {
    widgets: {
      items: [],
      fetching: false,
      fetched: false
    },
    tags: [],
    tag: {
      model: null,
      fetching: false,
      fetched: false,
      contents: {
        items: [],
        fetching: false,
        fetched: false,
        paging: {
          links: [],
          totalItems: 0,
          limit: 10
        }
      },
      latestContents: {
        items: [],
        fetching: false,
        fetched: false
      },
      profiles: {
        items: [],
        fetching: false,
        fetched: false
      }
    },
    tagArticles: {},
    hash: '',

    article: {
      model: {},
      fetching: false,
      fetched: false,
      request: {},
      notFound: false
    },
    articleComments: {
      items: [],
      paging: {
        links: [],
        totalItems: 0,
        limit: 5
      },
      request: {},
      fetching: false,
      fetched: false
    },
    recommendedArticles: {
      items: [],
      fetched: false,
      fetching: false
    },
    relatedQuestions: {
      items: [],
      fetched: false,
      fetching: false
    },
    relatedProducts: {
      items: [],
      fetched: false,
      fetching: false
    },
    // todo: revisar
    relatedGroupArticles: {
      items: [],
      fetched: false,
      fetching: false
    },

    groups: {
      items: [],
      paging: {
        links: [],
        totalItems: 0,
        limit: 24
      },
      request: {
        filters: {
          order: null
        }
      },
      fetching: false,
      fetched: false
    },
    enterpriseAdvisers: {
      items: [],
      fetching: false,
      fetched: false
    },
    videos: {
      items: [],
      paging: {
        links: [],
        totalItems: 0,
        limit: 12
      },
      request: {
        filters: {
          filter: null,
          author: null
        }
      },
      fetching: false,
      fetched: false,
      // Si la ultima consulta con filtro fallo. Sin esto, un filtro rechazado por la API dejaba la
      // pastilla marcada y la lista anterior en pantalla: el lector pulsaba y no pasaba nada.
      filterError: false
    },
    // Muestra sin filtrar de la que salen las pastillas de serie. Aparte de `videos` porque esa
    // lista se filtra, y las OPCIONES del filtro no pueden salir de una lista filtrada: al elegir
    // una serie la tira se quedaría con esa sola.
    //
    // `fromList` dice que la muestra ES la lista sin filtrar que hay en `videos` y no se copia: el
    // render de servidor serializaba las dos y la lista iba dos veces en el estado inicial —228 KB
    // de los 487 KB de /videos, medido el 2026-10-07—. La copia se hace solo en el momento en que
    // la lista va a dejar de ser la muestra, o sea al aplicar un filtro (ver SET_VIDEOS_FILTERS).
    multimediaFacets: {
      items: [],
      fetched: false,
      fromList: false
    },
    // Selección editorial de la portada de /videos y /podcasts. Es la MISMA consulta que `videos`
    // pero ordenada por `evergreen`, así que se guarda aparte: si compartieran slice, cada una
    // sobreescribiría la lista de la otra.
    // `forType` es el tipo para el que se pidieron: la escena no los vuelve a pedir si es el suyo.
    multimediaHighlights: {
      items: [],
      fetching: false,
      fetched: false,
      forType: null
    },
    homeVideos: {
      items: [],
      paging: {
        links: [],
        totalItems: 0,
        limit: 12
      },
      fetching: false,
      fetched: false
    },
    homePodcasts: {
      items: [],
      paging: {
        links: [],
        totalItems: 0,
        limit: 12
      },
      fetching: false,
      fetched: false
    },
    // todo: revisar
    banks: {
      items: [],
      fetching: false,
      fetched: false
    },
    questions: {},
    reviews: {}
  },
  action: Object
) {
  switch (action.type) {
    case 'SET_WIDGETS_FETCHING_STATUS': {
      return {
        ...state,
        widgets: {
          items: state.widgets.items,
          fetching: action.fetching,
          fetched: false
        }
      };
    }

    case 'SET_WIDGETS': {
      return {
        ...state,
        widgets: {
          items: action.widgets,
          fetching: false,
          fetched: true
        }
      };
    }

    case ACTIONS.SET_TAG_FETCHING_STATUS: {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        ...{
          fetching: action.fetching,
          fetched: action.fetched
        }
      };
      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG: {
      const newState = { ...state };

      if (!action.tag) {
        newState.tag = {
          ...state.tag,
          model: action.tag,
          fetching: false,
          fetched: true
        };

        return { ...state, ...newState };
      }

      newState.tag = {
        ...state.tag,
        model: action.tag,
        fetching: false,
        fetched: true
      };

      const NEW_TAG = {
        model: action.tag,
        videos: {
          items: [],
          paging: {
            links: [],
            totalItems: 0,
            limit: 24
          },
          request: {},
          fetching: false,
          fetched: true
        }
      };

      const tag = newState.tags.find(item => item.model.id === action.tag.id);

      const newTag = tag
        ? {
          ...tag,
          ...{
            model: action.tag
          }
        }
        : NEW_TAG;

      newState.tags = [...newState.tags.filter(item => item.model.id !== action.tag.id), newTag];

      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_ENTITIES_FETCHING_STATUS: {
      const newState = { ...state };
      const tagIndex = newState.tags.findIndex(item => item.model.id === action.entityId);

      newState.tags[tagIndex][action.contentType].fetching = action.fetching;
      newState.tags[tagIndex][action.contentType].fetched = action.fetched;
      newState.hash += '-fetching';

      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_VIDEOS: {
      const newState = { ...state };
      const tagIndex = newState.tags.findIndex(item => item.model.id === action.tagId);
      const newTag = { ...newState.tags[tagIndex] };

      newTag.videos = {
        ...newTag.videos,
        ...{
          items: [...action.videos],
          fetching: false,
          fetched: true
        }
      };
      newState.tags[tagIndex] = { ...newTag };
      newState.hash = newState.tags[tagIndex].videos.items.map(item => item.id).join(',');

      return { ...state, ...newState };
    }

    case 'SET_TAGS': {
      const newState = { ...state };
      newState.tags = [...action.tags];
      return { ...state, ...newState };
    }

    case 'CLEAR_TAG': {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        model: null,
        fetching: false,
        fetched: false
      };
      return { ...state, ...newState };
    }

    case 'SET_ARTICLE': {
      const newState = { ...state };
      newState.article.model = {
        ...action.article,
        ...{
          body: {
            ...action.article.body,
            text: base64Encode(action.article.body.text)
          },
          metadata: { ...action.article.body.metadata },
          url: action.article.body.url
        }
      };
      newState.article.fetching = false;
      newState.article.fetched = true;
      newState.article.notFound = false;

      return { ...state, ...newState };
    }

    case 'CLEAR_ARTICLE': {
      const newState = { ...state };
      newState.article.model = {};
      newState.article.fetching = false;
      newState.article.fetched = false;
      newState.article.notFound = false;

      newState.articleComments = {
        items: [],
        paging: {
          links: [],
          totalItems: 0,
          limit: 5
        },
        request: {},
        fetching: false,
        fetched: false
      };

      return { ...state, ...newState };
    }

    case 'SET_ARTICLE_NOT_FOUND': {
      const newState = { ...state };

      newState.article.fetching = false;
      newState.article.fetched = true;
      newState.article.notFound = true;

      return { ...state, ...newState };
    }

    case 'SET_ARTICLE_COMMENTS': {
      const newState = { ...state };
      const oldComments = [...state.articleComments.items];

      newState.articleComments.items = [...action.comments, ...oldComments];
      newState.articleComments.paging = action.paging;
      newState.articleComments.fetching = false;
      newState.articleComments.fetched = true;

      return { ...state, ...newState };
    }

    case 'SET_ARTICLE_USER_COMMENT': {
      const newState = { ...state };
      const oldComments = [...state.articleComments.items];

      newState.articleComments.items = [...oldComments, action.comment];
      newState.articleComments.paging.totalItems += 1;
      newState.article.model.stats.comments += 1;

      return { ...state, ...newState };
    }

    case 'SET_ARTICLE_COMMENTS_FETCHING_STATUS': {
      const newState = { ...state };

      newState.articleComments.fetching = action.fetching;
      newState.articleComments.fetched = action.fetched;

      return { ...state, ...newState };
    }

    case 'SET_RECOMMENDED_ARTICLES': {
      return {
        ...state,
        recommendedArticles: {
          items: action.articles,
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_RECOMMENDED_ARTICLES_FETCHING_STATUS': {
      return {
        ...state,
        recommendedArticles: {
          items: state.recommendedArticles.items,
          fetching: action.fetching,
          fetched: action.fetched
        }
      };
    }

    case 'SET_RELATED_QUESTIONS': {
      return {
        ...state,
        relatedQuestions: {
          items: action.questions,
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_RELATED_QUESTIONS_FETCHING_STATUS': {
      return {
        ...state,
        relatedQuestions: {
          items: state.relatedQuestions.items,
          fetching: action.fetching,
          fetched: action.fetched
        }
      };
    }

    case 'SET_RELATED_PRODUCTS': {
      const newState = { ...state };
      newState.relatedProducts.items = action.products;
      newState.relatedProducts.fetching = false;
      newState.relatedProducts.fetched = true;

      return { ...state, ...newState };
    }

    case 'SET_RELATED_PRODUCTS_FETCHING_STATUS': {
      const newState = { ...state };

      newState.relatedProducts.fetching = action.fetching;
      newState.relatedProducts.fetched = action.fetched;

      return { ...state, ...newState };
    }

    case 'SET_RELATED_GROUP_ARTICLES': {
      return {
        ...state,
        relatedGroupArticles: {
          items: action.articles,
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_RELATED_GROUP_ARTICLES_FETCHING_STATUS': {
      return {
        ...state,
        relatedGroupArticles: {
          items: state.relatedGroupArticles.items,
          fetching: action.fetching,
          fetched: action.fetched
        }
      };
    }

    case 'SET_GROUPS': {
      const newState = { ...state };

      const oldGroups = [...state.groups.items];

      const oldGroupsIds = oldGroups.map(item => item.id);

      newState.groups.items = [
        ...oldGroups,
        ...action.groups
          .filter(group => !oldGroupsIds.includes(group.id))
          .map(item => ({
            ...item,
            ...{ disclaimers: [] }
          }))
      ];
      newState.groups.paging = action.paging;
      newState.groups.fetching = false;
      newState.groups.fetched = true;

      return { ...state, ...newState };
    }

    case 'CLEAR_GROUPS': {
      const newState = { ...state };

      newState.groups.items = [];
      newState.groups.paging = null;
      newState.groups.fetching = false;
      newState.groups.fetched = false;
      return { ...state, ...newState };
    }

    case 'SET_GROUPS_FETCHING_STATUS': {
      const newState = { ...state };

      newState.groups.fetching = action.fetching;
      newState.groups.fetched = action.fetched;

      return { ...state, ...newState };
    }

    case 'SET_GROUPS_WITH_FILTERS': {
      const newState = { ...state };

      newState.groups.items = [...action.groups];
      newState.groups.paging = action.paging;
      newState.groups.fetching = false;
      newState.groups.fetched = true;

      return { ...state, ...newState };
    }

    case 'SET_GROUPS_FILTERS': {
      const newState = { ...state };
      newState.groups.request.filters.order = action.filters;
      newState.groups.fetching = true;
      return { ...state, ...newState };
    }

    case 'SET_ENTERPRISE_ADVISERS': {
      return {
        ...state,
        enterpriseAdvisers: {
          items: action.enterprises,
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_VIDEOS': {
      // Al paginar se descartan los ids que ya estaban. Hacían falta dos cosas para que aparecieran
      // repetidos y ahora las dos están: que la clave de orden cambiara entre páginas (arreglado en
      // getMultimediaNextPage) y que se publique algo entre dos cargas, que sigue pasando y desplaza
      // la ventana. OJO: el desplazamiento de la petición siguiente sale del `next` que devuelve el
      // servidor, no de items.length, así que descartar aquí no descuadra el cursor.
      const knownIds = new Set(action.replace ? [] : state.videos.items.map(item => item.id));
      const incoming = (action.videos || []).filter(item => !knownIds.has(item.id));

      // La primera página sin filtro vale además como muestra para las pastillas, así que no hace
      // falta pedirla dos veces ni copiarla: se marca `fromList` y quien la lee va a `videos.items`.
      // Con filtro no: esta lista ya está acotada y las opciones tienen que salir del catálogo
      // completo.
      const { filter, author } = state.videos.request.filters;
      const isSample = action.replace && !filter && !author;

      return {
        ...state,
        videos: {
          ...state.videos,
          paging: action.paging,
          items: action.replace ? [...action.videos] : [...state.videos.items, ...incoming],
          fetching: false,
          fetched: true
        },
        multimediaFacets: isSample ? { items: [], fetched: true, fromList: true } : state.multimediaFacets
      };
    }

    case 'SET_MULTIMEDIA_FACETS': {
      return {
        ...state,
        multimediaFacets: { items: [...action.items], fetched: true, fromList: false }
      };
    }

    case 'SET_MULTIMEDIA_HIGHLIGHTS': {
      return {
        ...state,
        multimediaHighlights: {
          ...state.multimediaHighlights,
          items: [...action.items],
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_MULTIMEDIA_HIGHLIGHTS_FETCHING': {
      return {
        ...state,
        multimediaHighlights: {
          ...state.multimediaHighlights,
          fetching: action.fetching,
          forType: action.forType
        }
      };
    }

    case 'CLEAR_VIDEOS': {
      const newState = { ...state };

      newState.videos = {
        items: [],
        paging: {
          links: [],
          totalItems: 0,
          limit: 12
        },
        request: {
          filters: {
            filter: null,
            author: null
          }
        },
        fetching: false,
        fetched: false,
        filterError: false
      };
      newState.multimediaHighlights = { items: [], fetching: false, fetched: false, forType: null };
      newState.multimediaFacets = { items: [], fetched: false, fromList: false };

      return { ...state, ...newState };
    }

    case 'SET_VIDEOS_FETCHING_STATUS': {
      // Sin mutar, por lo mismo que SET_VIDEOS_FILTERS: la portada de /videos pinta el esqueleto en
      // funcion de este `fetching`.
      return {
        ...state,
        videos: { ...state.videos, fetched: action.fetched, fetching: action.fetching }
      };
    }

    case 'SET_VIDEOS_WITH_FILTERS': {
      return {
        ...state,
        videos: {
          ...state.videos,
          paging: action.paging,
          items: [...action.videos],
          fetching: false,
          fetched: true,
          filterError: false
        }
      };
    }

    // La consulta con filtro no se ha podido servir. La lista anterior se conserva —tirarla dejaria
    // la pantalla en blanco por un fallo que puede ser de un segundo— pero la pantalla lo dice y
    // ofrece reintentar. Es lo que pasa HOY en produccion al filtrar por serie de podcast: la API
    // devuelve 400 porque el campo `author` todavia no esta desplegado en api-v4-contents.
    // (Comprobado leyendo la respuesta de la peticion de la propia app por CDP. Un fetch a mano
    // desde la consola da 401 por otra razon: le falta la cabecera `key` que pone finectAPI.)
    case 'SET_VIDEOS_FILTER_ERROR': {
      return {
        ...state,
        videos: { ...state.videos, fetching: false, fetched: true, filterError: true }
      };
    }

    case 'SET_VIDEOS_FILTERS': {
      // Sin mutar. `{ ...state }` no clona `videos`, asi que las dos asignaciones de antes escribian
      // sobre el MISMO objeto del estado anterior. Funcionaba de milagro —la raiz cambiaba y connect
      // volvia a leer— pero la portada ahora depende de este `fetching` para pintar el esqueleto al
      // cambiar de serie, y con un objeto compartido cualquier comparacion por referencia falla.
      //
      // Si la muestra era la propia lista, este es el momento de copiarla: la consulta que sigue va
      // a sustituir `videos.items` por la lista filtrada, y las pastillas tienen que seguir saliendo
      // de la lista entera. Ver `fromList` en el estado inicial.
      const { multimediaFacets } = state;

      return {
        ...state,
        multimediaFacets: multimediaFacets.fromList
          ? { items: [...state.videos.items], fetched: true, fromList: false }
          : multimediaFacets,
        videos: {
          ...state.videos,
          fetching: true,
          filterError: false,
          request: {
            ...state.videos.request,
            filters: {
              ...state.videos.request.filters,
              filter: action.filters.tag || null,
              author: action.filters.author || null
            }
          }
        }
      };
    }

    case 'SET_HOME_VIDEOS': {
      return {
        ...state,
        homeVideos: {
          ...state.homeVideos,
          paging: action.paging,
          items: action.replace ? [...action.homeVideos] : [...state.homeVideos.items, ...action.homeVideos],
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_HOME_VIDEOS_FETCHING_STATUS': {
      const newState = { ...state };

      newState.homeVideos.fetched = action.fetched;
      newState.homeVideos.fetching = action.fetching;

      return { ...state, ...newState };
    }

    case 'SET_HOME_PODCASTS': {
      return {
        ...state,
        homePodcasts: {
          ...state.homePodcasts,
          paging: action.paging,
          items: action.replace ? [...action.homePodcasts] : [...state.homePodcasts.items, ...action.homePodcasts],
          fetching: false,
          fetched: true
        }
      };
    }

    case 'SET_HOME_PODCASTS_FETCHING_STATUS': {
      const newState = { ...state };

      newState.homePodcasts.fetched = action.fetched;
      newState.homePodcasts.fetching = action.fetching;

      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_CONTENTS: {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        contents: {
          items: [...action.add ? state.tag.contents.items : [], ...action.contents.map(contentModelize)],
          fetching: false,
          fetched: true,
          paging: action.paging
        }
      };

      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_CONTENTS_FETCHING_STATUS: {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        contents: {
          ...state.tag.contents,
          fetching: action.fetching
        }
      };
      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_LATEST_CONTENTS: {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        latestContents: {
          items: [...action.contents.map(contentModelize)],
          fetching: false,
          fetched: true
        }
      };
      return { ...state, ...newState };
    }

    case ACTIONS.SET_TAG_LATEST_CONTENTS_FETCHING_STATUS: {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        latestContents: {
          ...state.tag.latestContents,
          fetching: action.fetching
        }
      };
      return { ...state, ...newState };
    }

    case 'SET_ADVISERS_PROFILES': {
      const newState = { ...state };
      newState.tag = {
        ...state.tag,
        profiles: {
          items: action.profiles,
          fetching: false,
          fetched: true
        }
      };
      return { ...state, ...newState };
    }

    // Questions
    case 'SET_QUESTIONS': {
      const newState = { ...state };

      if (!newState.questions[action.id]) {
        newState.questions[action.id] = {
          items: [],
          paging: {},
          request: {},
          fetching: false,
          fetched: false
        };
      }

      const data = { ...newState.questions[action.id] };

      data.items = action.request.offset ? [...data.items, ...action.items] : [...action.items];

      data.paging = action.paging;
      data.fetching = false;
      data.fetched = true;

      const newObject = {
        [action.id]: data
      };

      newState.questions = { ...newState.questions, ...newObject };
      return { ...state, ...newState };
    }

    // Reviews
    case 'SET_REVIEWS_FETCHING_STATUS': {
      const newState = { ...state };

      if (!newState.reviews[action.id]) {
        newState.reviews[action.id] = {
          items: [],
          paging: {},
          request: {},
          fetching: false,
          fetched: false
        };
      }

      const data = {
        ...newState.reviews[action.id],
        ...{
          fetching: action.fetching,
          fetched: action.fetched
        }
      };

      const newObject = {
        ...{
          [action.id]: { ...data }
        }
      };

      return {
        ...state,
        ...{
          reviews: {
            ...newState.reviews,
            ...newObject
          }
        }
      };
    }

    case 'SET_REVIEWS': {
      const newState = { ...state };

      if (!newState.reviews[action.id]) {
        newState.reviews[action.id] = {
          items: [],
          paging: {},
          request: {},
          fetching: false,
          fetched: false
        };
      }

      const data = { ...newState.reviews[action.id] };

      const newItems = action.request.offset ? [...data.items, ...action.items] : [...action.items];

      const newReview = {
        ...{
          fetching: false,
          fetched: true
        },
        ...{
          items: [...newItems],
          paging: action.paging,
          request: action.request
        }
      };

      const newReviews = {
        reviews: {
          ...newState.reviews,
          ...{
            [action.id]: newReview
          }
        }
      };

      return { ...state, ...newReviews };
    }

    case 'SET_ARTICLES_TAG_FETCHING_STATUS': {
      const newState = { ...state };

      if (!newState.tagArticles[action.id]) {
        newState.tagArticles[action.id] = {
          items: [],
          paging: {},
          fetching: action.fetching,
          fetched: false
        };
      }

      const data = { ...newState.tagArticles[action.id] };
      data.fetching = action.fetching;

      const newObject = {
        [action.id]: data
      };

      newState.tagArticles = { ...newState.tagArticles, ...newObject };
      return { ...state, ...newState };
    }

    case 'SET_ARTICLES_TAG': {
      const newState = { ...state };

      if (!newState.tagArticles[action.id]) {
        newState.tagArticles[action.id] = {
          items: [],
          paging: {},
          fetching: false,
          fetched: false
        };
      }

      const data = { ...newState.tagArticles[action.id] };
      data.items = [...data.items, ...action.contents];
      data.paging = action.paging;
      data.fetching = false;
      data.fetched = true;

      const newObject = {
        [action.id]: data
      };
      newState.tagArticles = { ...newState.tagArticles, ...newObject };

      return { ...state, ...newState };
    }

    case 'SET_BANKS_FETCHING_STATUS': {
      const newState = { ...state };
      newState.banks = {
        items: newState.banks.items,
        fetching: true,
        fetched: false
      };
      return { ...state, ...newState };
    }

    case 'SET_BANKS': {
      const newState = { ...state };
      newState.banks = {
        items: [
          ...action.banks.map(item => ({
            ...item,
            link: `/bancos/${item.alias}`,
            image: `/image/${item.icon}`
          }))
        ],
        fetching: false,
        fetched: true
      };
      return { ...state, ...newState };
    }

    case 'CLEAR_BANKS': {
      const newState = { ...state };
      newState.banks = {
        items: [],
        fetching: false,
        fetched: false
      };
      return { ...state, ...newState };
    }

    default:
      return state;
  }
}
